import type { MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import type {
  LoaderFunctionArgs,
  SerializeFrom,
} from '@remix-run/server-runtime'
import { json } from '@remix-run/server-runtime'
import { useMemo } from 'react'

import CollectionConfigurationForm from '~/components/collection/collection-configuration-form'
import { Seo } from '~/components/Seo'
import { makeTranslatedTitle } from '~/helpers/pages'
import { makeMainPath, makeShopPath } from '~/helpers/routes'
import { listAttachmentsOfTarget } from '~/models/attachment.server'
import { getCollectionStatus } from '~/models/collection.server'
import type { CollectionItem } from '~/models/collection_item.server'
import { listCollectionItemsOfCollection } from '~/models/collection_item.server'
import { recordVisit } from '~/models/event.server'
import type { ProductFull } from '~/models/product.server'
import { getProductAnalyticsByProductIds } from '~/models/product_analytic.server'
import { listAttachmentsOptionsSchema } from '~/schemas/attachment'
import { listCollectionItemsOptionsSchema } from '~/schemas/collection_item'
import {
  requireCollection,
  requireLiveCollection,
} from '~/services/auth-collection.server'
import { requireShopAndItsOptionalSeller } from '~/services/auth-shop.server'
import { getApexSession } from '~/services/session-apex.server'
import { SEO_IMAGE_OPTIONS } from '~/utils/constants'
import { getImageKitUrl, generateShopLogoFormats } from '~/utils/image'
import { parseSearchParams } from '~/utils/search-params'
import {
  generateCollectionSeoData,
  generateSeoImage,
  generateSeoMetaTags,
} from '~/utils/seo-helpers'

export type SerializedCollectionItemWithProduct =
  SerializeFrom<CollectionItem> & {
    product: SerializeFrom<ProductFull>
  }

export const loader = async (loaderArgs: LoaderFunctionArgs) => {
  const { shop, seller } = await requireShopAndItsOptionalSeller(
    loaderArgs.request
  )

  const untrustedCollectionId = loaderArgs.params.collectionId!

  const parsedSearchParams = parseSearchParams(
    loaderArgs,
    listCollectionItemsOptionsSchema
  )
  if (!seller) {
    // only sellers are allowed to pick a status other than live
    parsedSearchParams.status = 'live'
  } else if (parsedSearchParams.status == null) {
    parsedSearchParams.status = 'notArchived'
  }

  let parsedSearchShopSliderAttachmentParams = parseSearchParams(
    loaderArgs,
    listAttachmentsOptionsSchema
  )

  parsedSearchShopSliderAttachmentParams.targetId = untrustedCollectionId
  parsedSearchShopSliderAttachmentParams.targetType = 'generic'

  const [
    collection,
    { collectionItems },
    { attachments: collectionAttachments },
  ] = await Promise.all([
    seller
      ? requireCollection(shop, loaderArgs)
      : requireLiveCollection(shop, loaderArgs),
    listCollectionItemsOfCollection(untrustedCollectionId, parsedSearchParams),
    listAttachmentsOfTarget(shop, parsedSearchShopSliderAttachmentParams),
    !seller && recordVisit(untrustedCollectionId, loaderArgs.request.url),
  ])

  const collectionStatus = seller
    ? await getCollectionStatus(collection)
    : 'live'

  const sellerSession = await getApexSession(loaderArgs.request)
  const productsAnalytics = sellerSession.get('analyticsEnabled')
    ? await getProductAnalyticsByProductIds(
        collectionItems.map((ci) => ci.productId)
      )
    : undefined

  const validCollectionItems = collectionItems
    .filter((item) => item.product)
    .map((item) => ({
      id: item.id,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      disposition: item.disposition,
      productId: item.productId,
      collectionId: item.collectionId,
      product: item.product!,
    }))

  const collectionSeoData = generateCollectionSeoData(
    collection,
    validCollectionItems,
    shop
  )

  const collectionSeoImage = collection.avatarSrc
    ? getImageKitUrl(collection.avatarSrc).startsWith('http')
      ? generateSeoImage(collection.avatarSrc, SEO_IMAGE_OPTIONS)
      : `${process.env.APP_URL}${generateSeoImage(collection.avatarSrc, SEO_IMAGE_OPTIONS)}`
    : `${process.env.APP_URL}/gradient.png`

  const canonicalUrl = makeShopPath(
    shop,
    `/collections/${collection.id}/${collection.slug}`
  )

  // Generate shop favicon from theme color
  const shopLogos = await generateShopLogoFormats(shop.themeColor)
  const shopFaviconPng = shopLogos.png || makeMainPath(`/favicon.png`)
  const shopFaviconIco = shopLogos.ico || makeMainPath(`/favicon.ico`)

  return json({
    shop,
    title: await makeTranslatedTitle('page.shop_view_collection.title'),
    collection,
    collectionItems,
    collectionStatus,
    productsAnalytics,
    collectionAttachments,
    collectionSeoData,
    seoInfo: {
      canonicalUrl,
      collectionSeoImage,
      shopFaviconPng,
      shopFaviconIco,
    },
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.collection) {
    return [
      { title: 'Collection Not Found' },
      {
        name: 'description',
        content: 'The requested collection could not be found.',
      },
    ]
  }

  const { collection, shop, seoInfo } = data

  const description = collection.description || `${collection.name} collection`

  return generateSeoMetaTags(
    {
      title: `${collection.name} - ${shop.slug}`,
      seoTitle: collection.name,
      description,
      image: seoInfo.collectionSeoImage,
      canonicalUrl: seoInfo.canonicalUrl,
      ogType: 'collection',
      ogUrl: seoInfo.canonicalUrl,
      twitterImage: seoInfo.collectionSeoImage,
    },
    shop,
    seoInfo.shopFaviconPng,
    seoInfo.shopFaviconIco
  )
}

// collection information page
export default function CollectionViewPage() {
  const {
    collection,
    collectionItems,
    collectionAttachments,
    collectionSeoData,
  } = useLoaderData<typeof loader>()

  const collectionItemsSerialized: SerializedCollectionItemWithProduct[] =
    useMemo(() => {
      return collectionItems as SerializedCollectionItemWithProduct[]
    }, [collectionItems])

  return (
    <>
      <Seo seoData={collectionSeoData} />

      <CollectionConfigurationForm
        collectionDetails={{
          collection,
          collectionItemsSerialized,
          collectionAttachments,
        }}
      />
    </>
  )
}
